import { Injectable } from '@angular/core';
import { gql } from 'apollo-angular';
import { lastValueFrom } from 'rxjs';
import { Produit, ProduitFilterInput } from '../models/generated/graphql';
import { AuthService } from './auth-service.service';
import { QueryManagerService } from './queryManagerService';

const produits = gql`
  query produits($where: ProduitFilterInput) {
    produits(where: $where) {
      id
      nom
      nomTechniqueProduitBox
      enveloppeNavigation {
        code
        libelle
        backOfficeService
      }
      habilitationNavigation {
        id
        code
        libelle
      }
      produitExtension {
        souscriptionPossible
      }
    }
  }
`;

@Injectable({
  providedIn: 'root',
})
export class ProduitService {
  constructor(private queryManager: QueryManagerService, private userService: AuthService) {}

  async getProduits(): Promise<Produit[]> {
    const result = await lastValueFrom(
      this.queryManager.query<{ produits: Produit[] }>({
        query: produits,
      })
    );

    return result.data.produits;
  }

  /**
   * Fetches and returns a list of active products that are open for subscription.
   * Filters the products to only include those with possible subscription extensions.
   *
   * @returns {Promise<Produit[]>} A promise that resolves to an array of active products with possible subscription.
   */

  async getProduitsActifs(): Promise<Produit[]> {
    const result = await lastValueFrom(
      this.queryManager.query<{ produits: Produit[] }>({
        query: produits,
        variables: {
          where: <ProduitFilterInput>{
            and: [
              // { habilitation: { in: this.userService.getUserValidHabilitationIds() } },
              { etat: { eq: 2 } }, // etat: ouvert à la souscription
            ],
          },
        },
      })
    );

    return result.data.produits.filter(p => p.produitExtension?.souscriptionPossible);
  }

  /**
   * Renvoie les produits actifs dont le consultant a l'habilitation.
   * Seuls les produits avec souscription possible sont retournes.
   */
  async getProduitsActifsHabilites(): Promise<Produit[]> {
    const result = await lastValueFrom(
      this.queryManager.query<{ produits: Produit[] }>({
        query: produits,
        variables: {
          where: <ProduitFilterInput>{
            and: [
              { habilitation: { in: this.userService.getUserValidHabilitationIds() } },
              { etat: { eq: 2 } }, // etat: ouvert à la souscription
            ],
          },
        },
      })
    );

    return result.data.produits.filter(p => p.produitExtension?.souscriptionPossible);
  }
}
